import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import styles from "./index.module.scss"
import { Button, Checkbox, Input, message, Popover, Switch } from "antd"
import { LikeOutlined, DislikeOutlined, LeftOutlined, PlusOutlined, CloseOutlined } from '@ant-design/icons'
import Utils from '@/utils/utils'
import { MAX_TEMPORARY_MATERIALS } from '../hooks/useTemporaryMaterials'

const defaultAnnotations = [
]

export default function PicViewerImageInfo ({
    image = {},
    onCancel,
    activeExtraIndex = 0,
    maskVisible = false,
    onExtraImageSelect,
    onMaskVisibleChange,
    onFeedback,
    enableTemporaryMaterials = false,
    onTemporaryMaterialsAdd,
    onTemporaryMaterialRemove,
}) {
    const {
        title = '-',
        extraInfos = [],
        extraParams = [],
        extraImages = [],
        annotations = [],
        annotationHistory = defaultAnnotations,
        feedbackDisabled=true, // 是否禁止反馈按钮
        feedback=null, // 反馈内容
    } = image

    const activeExtraImage = extraImages[activeExtraIndex] || null
    const groupedExtraImages = useMemo(() => {
        const groups = []
        extraImages.forEach((item, index) => {
            if (item._temporaryMaterial) return
            const groupKey = item?.label || '__EMPTY_LABEL__'
            const prevGroup = groups[groups.length - 1]
            if (prevGroup && prevGroup.key === groupKey) {
                prevGroup.items.push({
                    ...item,
                    index,
                })
                return
            }
            groups.push({
                key: groupKey,
                label: item?.label,
                items: [{
                    ...item,
                    index,
                }],
            })
        })
        return groups
    }, [extraImages])

    const allAnnotations = useMemo(() => {
        let currentAnnotations = annotations?.filter(isCommentAnnotation) || []
        let currentAnnotationHistory = []
        if (activeExtraImage?.annotations?.length) {
            currentAnnotationHistory.push(...activeExtraImage.annotations.filter(isCommentAnnotation))
        } else {
            // 历史批注可能包含框选、涂画和快照，只在右侧“修改意见”中展示文字评论。
            currentAnnotationHistory.push(...annotationHistory.flatMap(group => group || []).filter(isCommentAnnotation))
        }
        const activeCommentSource = [
            ...currentAnnotations,
            ...currentAnnotationHistory,
        ]
        const currentComments = activeCommentSource
            .map(getCommentValue)
            .filter(Boolean)
        return currentComments
    }, [activeExtraImage, annotationHistory, annotations])

    const allAnnotationsLength = useMemo(() => allAnnotations.length, [allAnnotations])

    return (
        <div className={styles.PicViewerRight}>
            <div className={styles.Header}>
                <div className={styles.CloseButton} onClick={onCancel}>
                    <LeftOutlined />
                    <span>返回</span>
                </div>
            </div>
            <div className={styles.Section}>
                <div className={styles.Title}>图片详情</div>
                <div className={styles.Content}>
                    <div className={styles.InfoList}>
                        <div className={styles.InfoTitle}>{title}</div>
                        {extraInfos.map((item, i) => (
                            <div className={styles.InfoItem} style={{width: "100%"}} key={i}>
                                <div className={styles.Label}>{item.label}</div>
                                <div className={styles.Value}>{item.value || '-'}</div>
                            </div>
                        ))}
                        {groupedExtraImages.map((group, groupIndex) => (
                            <div className={styles.InfoItem} style={{width: "100%"}} key={`${group.key}-${groupIndex}`}>
                                <div className={styles.Label}>{group.label || '-'}</div>
                                <div className={styles.Value}>
                                    <div className={styles.ExtraImageGroup}>
                                        {group.items.map(item => (
                                            <div key={item.index} className={styles.ExtraImageCard}>
                                                <div
                                                    className={`${styles.ExtraImageButton} ${activeExtraIndex === item.index ? styles.active : ''}`}
                                                    onClick={() => onExtraImageSelect?.(item.index)}
                                                >
                                                {
                                                    isVideoUrl(item.url) ?
                                                    <img
                                                        src={Utils.getVideoSnapshot(item.url)}
                                                        alt={item.label || ''}
                                                        className={styles.SmallImg}
                                                    /> :
                                                    <img
                                                        src={Utils.getImgThumbnail(item.url)}
                                                        alt={item.label || ''}
                                                        className={styles.SmallImg}
                                                        />
                                                }
                                                </div>
                                                {item.maskUrl ? 
                                                <Checkbox
                                                    size="small"
                                                    checked={maskVisible}
                                                    onChange={e => onMaskVisibleChange(e.target.checked)}
                                                    disabled={activeExtraIndex != item.index}
                                                >
                                                    mask
                                                </Checkbox>
                                                : null}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        ))}
                        {enableTemporaryMaterials && <TemporaryMaterials
                            items={extraImages.map((item, index) => ({ ...item, index })).filter(item => item._temporaryMaterial)}
                            activeExtraIndex={activeExtraIndex}
                            onSelect={onExtraImageSelect}
                            onAdd={onTemporaryMaterialsAdd}
                            onRemove={onTemporaryMaterialRemove}
                        />}
                        {extraParams.map((item, i) => (
                            <div className={styles.InfoItem} style={{width: "100%"}} key={i}>
                                <div className={styles.Label}>{item.label}</div>
                                <div className={styles.Value}>{item.value || '-'}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
            {allAnnotationsLength > 0 &&
            <div className={`${styles.Section} ${styles.CommentSection}`}>
                <div className={styles.Title}>修改意见</div>
                <div className={`${styles.Content} ${styles.CommentContent}`}>
                    <div className={styles.InfoList}>
                        {allAnnotations.map((comment, i) => (
                            <div className={styles.InfoItem} key={i}>
                                {/* 修改意见index倒叙 */}
                                <div className={styles.Label}>修改意见{allAnnotations.length > 1 ? allAnnotations.length - i : ''}</div>
                                <div className={styles.Value}>{comment?.comment || '-'}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>}
            {feedbackDisabled ? null : <FeedbackRender feedback={feedback} onFeedback={onFeedback} />}
        </div>
    )
}

function TemporaryMaterials({ items, activeExtraIndex, onSelect, onAdd, onRemove }) {
    const inputRef = useRef(null)
    const [dragging, setDragging] = useState(false)
    return (
        <div className={styles.InfoItem}>
            <div className={styles.Label}>临时素材区</div>
            <div className={styles.TemporaryImageGroup}>
                {items.map(item => (
                    <div className={styles.ExtraImageCard} key={item.id}>
                        <button
                            type="button"
                            title={item.label}
                            aria-label={'预览临时素材：' + item.label}
                            aria-pressed={activeExtraIndex === item.index}
                            className={[styles.ExtraImageButton, activeExtraIndex === item.index ? styles.active : ''].join(' ')}
                            onClick={() => onSelect?.(item.index)}
                        >
                            <img src={item.url} alt={item.label} className={styles.SmallImg} />
                        </button>
                        <button
                            type="button"
                            className={styles.RemoveTemporaryImage}
                            aria-label={'移除临时素材：' + item.label}
                            onClick={() => onRemove?.(item.id)}
                        ><CloseOutlined /></button>
                    </div>
                ))}
            </div>
            {items.length < MAX_TEMPORARY_MATERIALS && <>
                <input
                    ref={inputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    hidden
                    aria-label="上传临时素材"
                    onChange={event => {
                        onAdd?.(event.target.files)
                        event.target.value = ''
                    }}
                />
                <button
                    type="button"
                    className={[styles.TemporaryUpload, dragging ? styles.dragging : ''].join(' ')}
                    onClick={() => inputRef.current?.click()}
                    onDragOver={event => {
                        event.preventDefault()
                        event.stopPropagation()
                        setDragging(true)
                    }}
                    onDragLeave={event => {
                        if (!event.currentTarget.contains(event.relatedTarget)) setDragging(false)
                    }}
                    onDrop={event => {
                        event.preventDefault()
                        event.stopPropagation()
                        setDragging(false)
                        onAdd?.(event.dataTransfer.files)
                    }}
                >
                    <PlusOutlined aria-hidden="true" />
                    <span>拖拽/点击上传</span>
                </button>
            </>}
        </div>
    )
}

const dislikeReasons = [
    "人体残缺",
    "面部扭曲",
    "模特不好看",
    "模特不符合需求",
    "场景不符合需求",
    "场景不美观"
]

function FeedbackRender (props) {
    const { feedback, onFeedback } = props
    const [popoverOpen, setPopoverOpen] = useState(false)
    const [feedbackText, setFeedbackText] = useState(feedback?.description || "")
    const isMountedRef = useRef(true)
    useEffect(() => {
        return () => {
            isMountedRef.current = false
        }
    }, [])
    useEffect(() => {
        setFeedbackText(feedback?.description || "")
    }, [feedback])
    // 点赞
    const isLike = useMemo(() => feedback && feedback?.like == 1, [feedback])
    const isDislike = useMemo(() => feedback && feedback?.like == 0, [feedback])
    const handleLike = useCallback(async (like) => {
        try {
            const feedbackId = feedback?.id
            await onFeedback?.(true, null, feedbackId)
            if (!isMountedRef.current) return
            message.success("反馈成功")
        } catch (err) {
            console.error(err)
        }
    }, [feedback, onFeedback])
    
    const handleFeedbackSubmit = useCallback(async () => {
        try {
            if (!feedbackText.trim()) return message.warning('请先填写修改意见')
            const feedbackId = feedback?.id
            await onFeedback?.(false, feedbackText, feedbackId)
            if (!isMountedRef.current) return
            setPopoverOpen(false)
            message.success("反馈成功")
        } catch (err) {
            console.error(err)
        }
    }, [feedbackText, onFeedback, feedback])
    
    return (
        <div className={`${styles.Section} ${styles.FeedbackSection}`}>
            <div className={`${styles.Title} ${styles.FeedbackTitle}`}>效果反馈</div>
            <div className={`${styles.Content} ${styles.FeedbackContent}`}>
                <div className={styles.FeedbackItem} style={{color: isLike ? "#006EFF" : "#888888"}} onClick={() => handleLike(true)}>
                    <LikeOutlined />
                    <span>好评</span>
                </div>
                <Popover
                    trigger="click"
                    open={popoverOpen}
                    onOpenChange={setPopoverOpen}
                    content={
                        <div
                            style={{
                                width: 385,
                                display:"flex",
                                flexDirection:"column",
                                gap:"12px",
                            }}
                        >
                            <div style={{fontSize: "16px", lineHeight: "30px", color: "#1D1B1B"}}>您对生成结果不满意的原因是什么？</div>
                            <div style={{display:"flex",flexWrap:"wrap", gap:"12px"}}>
                                {dislikeReasons.map((item, i) => (
                                    <div
                                        key={i}
                                        style={{
                                            cursor:"pointer",
                                            width:"120px",
                                            height:"32px",
                                            textAlign:"center",
                                            lineHeight:"32px",
                                            borderRadius:"8px",
                                            border: "1px solid #DEE2E8",
                                            boxSizing: "border-box",
                                            userSelect: "none",
                                        }}
                                        onClick={() => setFeedbackText(pre => pre ? [pre, item].join("、") : item)}
                                    >{item}</div>
                                ))}
                            </div>
                            <Input.TextArea
                                value={feedbackText}
                                placeholder="任何让您不满的原因，都请写在这边"
                                autoSize={{ minRows: 4, maxRows: 4 }}
                                onChange={e => setFeedbackText(e.target.value)}
                            />
                            <div style={{
                                display:"flex",
                                justifyContent:"space-between"
                            }}>
                                <Button style={{width: 178}} onClick={() => setPopoverOpen(false)}>取消</Button>
                                <Button style={{width: 178}} type="primary" onClick={handleFeedbackSubmit}>提交</Button>
                            </div>
                        </div>
                    }
                >
                    <div className={styles.FeedbackItem} style={{color: isDislike ? "#006EFF" : "#888888"}}>
                        <DislikeOutlined />
                        <span>差评</span>
                    </div>
                </Popover>
            </div>
        </div>
    )
}

// 修改意见, 兼容结构为 { label: string, timestamp: datetime } 或 { text: string, timestamp: datetime } 或 { comment: string, timestamp: datetime }
var isCommentAnnotation = (item) => {
    if (!item || typeof item !== 'object') return false
    return (!!item.label || !!item.text || !!item.comment) && !item.snapshot
}

const getCommentValue = (item) => {
    return {
        comment: item.comment || item.text || item.label,
        timestamp: item.timestamp,
    }
}

// 常见视频扩展名，用于判断 url 是否为视频资源
const VIDEO_EXTENSIONS = /\.(mp4|webm|ogg|mov|avi|mkv|flv|wmv|m4v)(\?|$)/i
/** 根据 url 扩展名判断是否为视频 */
function isVideoUrl(url) {
    if (!url) return false
    return VIDEO_EXTENSIONS.test(url)
}
